/**
 * SPRINT 3A - Mobile Event Sequence Logger
 * 
 * Instrumentação completa para rastrear a sequência de eventos do SpeechRecognition
 * em dispositivos móveis. O objetivo é identificar exatamente qual evento não ocorre
 * ou ocorre fora de ordem.
 * 
 * Eventos rastreados (em ordem esperada):
 * - onstart
 * - onspeechstart
 * - onsoundstart
 * - onaudiostart
 * - onresult (interim/final)
 * - onspeechend
 * - onsoundend
 * - onaudioend
 * - onend
 * - stop()
 * - abort()
 * - TurnDecisionEngine.receiveSignal()
 * - ConversationStateMachine.transition()
 * 
 * @author Vocalira Team
 * @version 1.0.0 - SPRINT 3A Investigation
 */

class MobileEventSequenceLogger {
  constructor() {
    this.sessions = [];
    this.currentSession = null;
    this.enabled = true;
    
    // Detectar plataforma
    this.platform = this._detectPlatform();
  }

  /**
   * Inicia uma nova sessão de logging
   */
  startSession() {
    this.currentSession = {
      id: `mevt_${Date.now()}`,
      platform: this.platform,
      startTime: Date.now(),
      events: [],
      expectedOrder: [
        'onstart',
        'onaudiostart', 
        'onsoundstart',
        'onspeechstart',
        'onresult_interim',
        'onresult_final',
        'onspeechend',
        'onsoundend',
        'onaudioend',
        'onend'
      ],
      missingEvents: [],
      outOfOrderEvents: [],
      summary: null,
    };
    
    console.log(`[MobileEventLogger] Session started: ${this.currentSession.id} on ${this.platform}`);
    return this.currentSession.id;
  }

  /**
   * Registra um evento
   */
  logEvent(eventName, data = {}) {
    if (!this.currentSession) {
      this.startSession();
    }
    
    const now = Date.now();
    const event = {
      name: eventName,
      timestamp: now,
      elapsedMs: now - this.currentSession.startTime,
      data,
      index: this.currentSession.events.length,
    };
    
    this.currentSession.events.push(event);
    
    // Log para console com cores para facilitar debug
    const color = this._getEventColor(eventName);
    console.log(
      `%c[MobileEventLogger] ${event.elapsedMs}ms: ${eventName}`,
      `color: ${color}; font-weight: bold;`,
      data
    );
    
    return event;
  }

  /**
   * Registra eventos específicos do SpeechRecognition
   */
  logRecognitionEvent(type, details = {}) {
    return this.logEvent(`recognition_${type}`, details);
  }

  /**
   * Registra eventos do TurnDecisionEngine
   */
  logTurnDecisionEvent(type, details = {}) {
    return this.logEvent(`turnDecision_${type}`, details);
  }

  /**
   * Registra eventos do ConversationStateMachine
   */
  logStateMachineEvent(type, details = {}) {
    return this.logEvent(`stateMachine_${type}`, details);
  }

  /**
   * Finaliza a sessão e gera análise
   */
  endSession() {
    if (!this.currentSession) return null;
    
    const session = this.currentSession;
    session.endTime = Date.now();
    session.duration = session.endTime - session.startTime;
    
    // Analisar sequência de eventos
    session.summary = this._analyzeEventSequence(session);
    
    this.sessions.push(session);
    this.currentSession = null;
    
    // Log do resumo
    console.log('%c[MobileEventLogger] Session Summary:', 'color: #FF5722; font-weight: bold;');
    console.table(session.events.map(e => ({
      time: `${e.elapsedMs}ms`,
      event: e.name,
      data: JSON.stringify(e.data).substring(0, 50)
    })));
    
    if (session.summary.missingEvents.length > 0) {
      console.warn('[MobileEventLogger] MISSING EVENTS:', session.summary.missingEvents);
    }
    
    if (session.summary.outOfOrderEvents.length > 0) {
      console.warn('[MobileEventLogger] OUT OF ORDER:', session.summary.outOfOrderEvents);
    }
    
    console.log('[MobileEventLogger] Pipeline Status:', session.summary.pipelineStatus);
    
    return session;
  }

  /**
   * Analisa a sequência de eventos
   */
  _analyzeEventSequence(session) {
    const receivedEvents = session.events.map(e => e.name);
    
    // Eventos de reconhecimento esperados
    const recognitionEvents = [
      'recognition_onstart',
      'recognition_onaudiostart',
      'recognition_onsoundstart', 
      'recognition_onspeechstart',
      'recognition_onresult_interim',
      'recognition_onresult_final',
      'recognition_onspeechend',
      'recognition_onsoundend',
      'recognition_onaudioend',
      'recognition_onend'
    ];
    
    // Verificar quais eventos estão faltando
    const missingEvents = recognitionEvents.filter(expected => {
      // Para onresult, verificar se há pelo menos um
      if (expected === 'recognition_onresult_interim') {
        return !receivedEvents.some(e => e.startsWith('recognition_onresult_interim'));
      }
      if (expected === 'recognition_onresult_final') {
        return !receivedEvents.some(e => e.startsWith('recognition_onresult_final'));
      }
      return !receivedEvents.includes(expected);
    });
    
    // Verificar ordem dos eventos
    const outOfOrderEvents = [];
    let lastOrderIndex = -1;
    
    for (const event of session.events) {
      const expectedIndex = recognitionEvents.indexOf(event.name);
      if (expectedIndex !== -1) {
        if (expectedIndex < lastOrderIndex) {
          outOfOrderEvents.push({
            event: event.name,
            expectedAfter: recognitionEvents[lastOrderIndex],
            receivedAt: event.elapsedMs
          });
        }
        lastOrderIndex = Math.max(lastOrderIndex, expectedIndex);
      }
    }
    
    // Determinar ponto de falha na pipeline
    let pipelineStatus = 'complete';
    let failurePoint = null;
    
    if (missingEvents.includes('recognition_onstart')) {
      pipelineStatus = 'failed_at_start';
      failurePoint = 'SpeechRecognition never started';
    } else if (missingEvents.includes('recognition_onresult_final')) {
      pipelineStatus = 'no_final_result';
      failurePoint = 'STT never returned final result';
    } else if (missingEvents.includes('recognition_onspeechend')) {
      pipelineStatus = 'no_speechend';
      failurePoint = 'onspeechend never fired (common mobile issue)';
    } else if (missingEvents.includes('recognition_onaudioend')) {
      pipelineStatus = 'no_audioend';
      failurePoint = 'onaudioend never fired';
    } else if (missingEvents.includes('recognition_onend')) {
      pipelineStatus = 'no_onend';
      failurePoint = 'recognition never properly ended';
    }
    
    // Verificar se TurnDecision recebeu os sinais
    const turnDecisionEvents = session.events.filter(e => e.name.startsWith('turnDecision_'));
    const hasDecisionComplete = turnDecisionEvents.some(e => e.name === 'turnDecision_complete');
    
    if (!hasDecisionComplete && pipelineStatus === 'complete') {
      pipelineStatus = 'decision_not_triggered';
      failurePoint = 'TurnDecisionEngine never reached decision threshold';
    }
    
    // Verificar StateMachine
    const stateMachineEvents = session.events.filter(e => e.name.startsWith('stateMachine_'));
    const hasProcessingState = stateMachineEvents.some(e => 
      e.data?.toState === 'PROCESSING' || e.data?.toState === 'LLM_STREAMING'
    );
    
    if (!hasProcessingState && pipelineStatus === 'complete') {
      pipelineStatus = 'never_reached_processing';
      failurePoint = 'ConversationStateMachine never transitioned to PROCESSING';
    }
    
    return {
      totalEvents: session.events.length,
      duration: session.duration,
      missingEvents,
      outOfOrderEvents,
      pipelineStatus,
      failurePoint,
      receivedEvents: receivedEvents.filter(e => e.startsWith('recognition_')),
      turnDecisionEvents: turnDecisionEvents.map(e => e.name),
      stateMachineTransitions: stateMachineEvents.map(e => e.data?.transition || e.name),
    };
  }

  /**
   * Retorna cor para o evento no console
   */
  _getEventColor(eventName) {
    if (eventName.includes('error')) return '#F44336';
    if (eventName.includes('onstart')) return '#4CAF50';
    if (eventName.includes('onend')) return '#FF9800';
    if (eventName.includes('onresult')) return '#2196F3';
    if (eventName.includes('speech')) return '#9C27B0';
    if (eventName.includes('audio')) return '#00BCD4';
    if (eventName.includes('turnDecision')) return '#E91E63';
    if (eventName.includes('stateMachine')) return '#673AB7';
    return '#607D8B';
  }

  /**
   * Detecta plataforma
   */
  _detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isSamsung = /SamsungBrowser/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !/Edge/i.test(ua);
    const isEdge = /Edg/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !isChrome;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    if (isPWA) {
      if (isIOS) return 'pwa-ios';
      if (isAndroid) return 'pwa-android';
      return 'pwa-desktop';
    }
    if (isIOS && isSafari) return 'ios-safari';
    if (isIOS && isChrome) return 'ios-chrome';
    if (isAndroid && isSamsung) return 'android-samsung';
    if (isAndroid && isChrome) return 'android-chrome';
    if (isEdge) return 'desktop-edge';
    if (isChrome) return 'desktop-chrome';
    if (isSafari) return 'desktop-safari';
    
    return 'unknown';
  }

  /**
   * Exporta todas as sessões para análise
   */
  exportData() {
    return JSON.stringify({
      exportedAt: new Date().toISOString(),
      platform: this.platform,
      totalSessions: this.sessions.length,
      sessions: this.sessions,
    }, null, 2);
  }

  /**
   * Retorna estatísticas agregadas
   */
  getAggregatedStats() {
    const stats = {
      totalSessions: this.sessions.length,
      byPipelineStatus: {},
      commonMissingEvents: {},
      commonFailurePoints: {},
      avgDuration: 0,
    };
    
    let totalDuration = 0;
    
    for (const session of this.sessions) {
      if (!session.summary) continue;
      
      // Count pipeline status
      const status = session.summary.pipelineStatus;
      stats.byPipelineStatus[status] = (stats.byPipelineStatus[status] || 0) + 1;
      
      // Count missing events
      for (const event of session.summary.missingEvents) {
        stats.commonMissingEvents[event] = (stats.commonMissingEvents[event] || 0) + 1;
      }
      
      // Count failure points
      if (session.summary.failurePoint) {
        stats.commonFailurePoints[session.summary.failurePoint] = 
          (stats.commonFailurePoints[session.summary.failurePoint] || 0) + 1;
      }
      
      totalDuration += session.duration || 0;
    }
    
    stats.avgDuration = this.sessions.length > 0 
      ? Math.round(totalDuration / this.sessions.length) 
      : 0;
    
    return stats;
  }

  /**
   * Limpa dados
   */
  clear() {
    this.sessions = [];
    this.currentSession = null;
  }
}

// Instância global
const mobileEventLogger = new MobileEventSequenceLogger();

export default mobileEventLogger;
export { MobileEventSequenceLogger };
